import { useEffect, useMemo } from "react";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { PageLevelTypes } from "@enums/page-level-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { GeneralAdvancedValidationErrors } from "@idw-types/common/validation/common/general-advanced-validation-errors";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationServiceOffering
} from "@idw-types/data/rp-service-configuration";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { FormattedWorkflowDetailsConfiguration } from "@idw-types/data/workflow-details-configuration";
import { getSessionTokenInfo, initializeSessionTimeoutProperties } from "@utils/custom-session-timeout/custom-session-timeout-util";
import { isNullOrUndefined } from "@utils/object-util";
import { getTmxEventType, hasTmxServiceOffering } from "@utils/tmx-event-type/tmx-event-type-util";
import { CustomSessionTimeoutContainer } from "@containers/common/properties-sections/custom-session-timeout-container/custom-session-timeout-container";
import { PrefilledCredentialContainer } from "@containers/common/properties-sections/prefilled-credential-container/prefilled-credential-container";
import { TmxEventTypeContainer } from "@containers/common/properties-sections/tmx-event-type-container/tmx-event-type-container";
import { WorkflowWebhookConfigurationContainer } from "./workflow-webhook-configuration-container/workflow-webhook-configuration-container";
import { WorkflowLoginHintContainer } from "./workflow-login-hint-container/workflow-login-hint-container";
import { WorkflowIncodeSmsContainer } from "@containers/workflow-details/workflow-details-component/workflow-details-general-tab/advanced-tab-container/workflow-incode-sms-container/workflow-incode-sms-container";
import { WorkflowRedirectToCallBackUrlOnErrorContainer } from "@containers/workflow-details/workflow-details-component/workflow-details-general-tab/advanced-tab-container/workflow-redirect-to-callback-url-on-error-container/workflow-redirect-to-callback-url-on-error-container";

type AdvancedTabContainerProps = {
    isDisabled: boolean;
    isAdminUser: boolean;
    validationErrors: GeneralAdvancedValidationErrors;
    workflowDetailsConfigurationState: FormattedWorkflowDetailsConfiguration;
    primaryRpService: FormattedRpServiceConfiguration;
    rpServiceConfigurations: Array<FormattedRpServiceConfiguration>;
    axnDocumentationLinks: Array<AxnDocumentationLink>;
    showIncodeWidget: boolean;
    updateProperties: (properties: Array<{ propertyTypeId: string, newValue: string, added: boolean }>) => void;
};

export const AdvancedTabContainer = (props: AdvancedTabContainerProps) => {
    const {
        isDisabled,
        isAdminUser,
        validationErrors,
        workflowDetailsConfigurationState,
        axnDocumentationLinks,
        primaryRpService,
        showIncodeWidget,
        rpServiceConfigurations,
        updateProperties
    } = props;

    const primaryApiKey = useMemo(() => {
        const primaryService = (workflowDetailsConfigurationState.rpServiceWorkflows || []).find(service => isStringTrue(service.primaryServiceFlag));
        if (primaryService) {
            return primaryService.apiKey;
        }
        return "";
    }, [workflowDetailsConfigurationState.rpServiceWorkflows]);

    const { asiTimeoutPropertyValue, customTokenTimeoutPropertyValue } = useMemo(() => {
        return getSessionTokenInfo([], workflowDetailsConfigurationState.appliedProfiles);
    }, [workflowDetailsConfigurationState.appliedProfiles]);

    const tmxEventTypePropertyValue = useMemo(() => {
        return getTmxEventType([], workflowDetailsConfigurationState.appliedProfiles);
    }, [workflowDetailsConfigurationState.appliedProfiles]);

    const showTmxEventTypeWidget = useMemo(() => {
        const serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering> = [];
        rpServiceConfigurations.forEach(rpService => {
            const rpServiceOfferings = rpService.serviceOfferings.filter(so => so.status === EntityStatusTypes.ACTIVE && so.addedToService);
            rpServiceOfferings.forEach(rpso => {
                serviceOfferings.push(rpso);
            });
        });
        return hasTmxServiceOffering(serviceOfferings);
    }, [rpServiceConfigurations]);

    const showSessionTimeoutWidget = useMemo(() => {
        return !isNullOrUndefined(asiTimeoutPropertyValue) || !isNullOrUndefined(customTokenTimeoutPropertyValue);
    }, [asiTimeoutPropertyValue, customTokenTimeoutPropertyValue]);
    
    useEffect(() => {
        if (!isDisabled) {
            initializeSessionTimeoutProperties(
                asiTimeoutPropertyValue,
                customTokenTimeoutPropertyValue,
                updateProperties
            );
        }
    }, []);

    return (
        <div>
            <WorkflowLoginHintContainer
                isDisabled={isDisabled}
                validationErrors={validationErrors}
                documentationLinks={axnDocumentationLinks}
                workflowDetailsConfigurationState={workflowDetailsConfigurationState}
                updateProperties={updateProperties}
            />

            {showIncodeWidget && <WorkflowIncodeSmsContainer
                isDisabled={isDisabled}
                documentationLinks={axnDocumentationLinks}
                workflowDetailsConfigurationState={workflowDetailsConfigurationState}
                incodeSmsMessagingServiceError={validationErrors.incodeSmsMessagingServiceError}
                updateProperties={updateProperties}
            />}

            {showSessionTimeoutWidget &&
                <CustomSessionTimeoutContainer
                    isDisabled={isDisabled || !workflowDetailsConfigurationState.permissions.canModifyNonRestrictedProperties}
                    validationErrors={validationErrors}
                    documentationLinks={axnDocumentationLinks}
                    asiTimeoutPropertyValue={asiTimeoutPropertyValue}
                    inheritedAsiTimeoutPropertyValue={null}
                    inheritedCustomTokenTimeoutPropertyValue={null}
                    pageLevel={PageLevelTypes.WORKFLOW}
                    workflowId={workflowDetailsConfigurationState.id}
                    customTokenTimeoutPropertyValue={customTokenTimeoutPropertyValue}
                    updateProperties={updateProperties}
                />
            }
            <PrefilledCredentialContainer
                isDisabled={true}
                validationErrors={validationErrors}
                documentationLinks={axnDocumentationLinks}
                updateAttributes={() => { }}
                updateProperties={() => { }}
                properties={primaryRpService.properties}
                primaryServiceFormattedAttributes={primaryRpService.attributes}
            />
            <WorkflowWebhookConfigurationContainer
                isDisabled={isDisabled}
                validationErrors={validationErrors}
                apiKey={primaryApiKey}
                documentationLinks={axnDocumentationLinks}
                workflowDetailsConfigurationState={workflowDetailsConfigurationState}
                updateProperties={updateProperties}
            />
            {showTmxEventTypeWidget && (
                <TmxEventTypeContainer
                    isDisabled={isDisabled || !isAdminUser}
                    inheritedTmxEventType={null}
                    tmxEventType={tmxEventTypePropertyValue}
                    pageLevel={PageLevelTypes.WORKFLOW}
                    workflowId={workflowDetailsConfigurationState.id}
                    updateProperties={updateProperties}
                />
            )}
            <WorkflowRedirectToCallBackUrlOnErrorContainer
                documentationLinks={axnDocumentationLinks}
                workflowDetailsConfigurationState={workflowDetailsConfigurationState}
                isDisabled={isDisabled || !isAdminUser}
                updateProperties={updateProperties}
            />
        </div>
    );
};